import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    TextField,
    IconButton,
    Alert,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    InputAdornment,
    Snackbar,
    Chip,
    Avatar,
    alpha,
    useTheme,
    Tooltip,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Category as CategoryIcon,
    Inventory as InventoryIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import useSmartSearch from '../../hooks/useSmartSearch';
import ProductDialog from './ProductDialog';
import InventorySetupDialog from './InventorySetupDialog';
import PageContainer from '../shared/PageContainer';

const ProductManagement = () => {
    useAuth(); // Auth context for protected route
    const theme = useTheme();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Memoize category ID to prevent unnecessary re-renders
    const selectedCategoryId = useMemo(() => selectedCategory?.id || null, [selectedCategory?.id]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Smart search hook for products
    const {
        searchTerm,
        setSearchTerm,
        results: products,
        loading,
        error: searchError,
        isSearchPending,
        refreshCache
    } = useSmartSearch('/products', {
        searchFields: ['name', 'sku', 'description'],
        categoryId: selectedCategoryId,
        preloadActive: true, // Load products with inventory first
        // Don't pass branchId for products page - show aggregated inventory across all branches
        branchId: null
    });

    // Dialog states
    const [productDialog, setProductDialog] = useState({ open: false, product: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null });
    const [inventorySetupDialog, setInventorySetupDialog] = useState({ open: false, product: null });

    // Menu states
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Combine errors
    const combinedError = error || searchError;

    const fetchCategories = async () => {
        try {
            const response = await api.get('/products/categories');
            setCategories(response.data.categories || []);
            setError('');
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
            setError('Failed to fetch categories. Please try again.');
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    // Handle actions
    const handleProductAction = (action, product = null) => {
        if (categories.length === 0) {
            setError('You must create at least one category before adding products.');
            return;
        }
        setSelectedProduct(product);
        setProductDialog({ open: true, product });
        setMenuAnchor(null);
    };

    const handleDialogClose = (productData = null, isUpdate = false) => {
        setProductDialog({ open: false, product: null });
        setError('');

        // If product data was returned (either created or updated)
        if (productData) {
            refreshCache(); // Refresh the cached product list

            if (isUpdate) {
                // For updates, show success message
                setSuccess('Product updated successfully');
            } else {
                // For new products, show inventory setup dialog
                setInventorySetupDialog({ open: true, product: productData });
            }
        }
    };

    const handleDelete = async () => {
        try {
            const response = await api.delete(`/products/${deleteDialog.product.id}`);

            // Show success message
            setError('');
            const productName = deleteDialog.product.name;
            setDeleteDialog({ open: false, product: null });
            refreshCache(); // Refresh cached data

            // Display success message to user
            const successMessage = response.data.message || `Product "${productName}" processed successfully`;
            setSuccess(successMessage);

        } catch (error) {
            console.error('Error deleting product:', error);

            // Extract error message from response
            const errorMessage = error.response?.data?.message || 'Failed to delete product. Please try again.';
            setError(errorMessage);

            // Don't close the dialog so user can see the error
            // setDeleteDialog({ open: false, product: null });
        }
    };

    const handleCategoriesClick = () => {
        navigate('/app/categories');
    };

    // Stock status helper
    const getStockStatus = (total, hasInventory) => {
        if (!hasInventory) return { label: 'Not Stocked', color: theme.palette.text.disabled, bgColor: alpha(theme.palette.text.disabled, 0.08) };
        if (total === 0) return { label: 'Out of Stock', color: theme.palette.error.main, bgColor: alpha(theme.palette.error.main, 0.08) };
        if (total <= 10) return { label: 'Low Stock', color: theme.palette.warning.main, bgColor: alpha(theme.palette.warning.main, 0.08) };
        return { label: 'In Stock', color: theme.palette.success.main, bgColor: alpha(theme.palette.success.main, 0.08) };
    };

    // Category color helper - generates consistent colors per category
    const getCategoryColor = (categoryName) => {
        const colors = [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.info.main,
            theme.palette.success.main,
            theme.palette.warning.main,
        ];
        if (!categoryName) return colors[0];
        const hash = categoryName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return colors[hash % colors.length];
    };

    // DataGrid columns with flexible layout
    const columns = [
        {
            field: 'sku',
            headerName: 'SKU',
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'name',
            headerName: 'Product Name',
            flex: 1.5,
            minWidth: 200,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.4 }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'category_name',
            headerName: 'Category',
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                const catColor = getCategoryColor(params.value);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Chip
                            label={params.value || 'Uncategorized'}
                            size="small"
                            sx={{
                                height: 26,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                bgcolor: alpha(catColor, 0.1),
                                color: catColor,
                                border: 'none',
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            field: 'total_stock',
            headerName: 'Total Stock',
            flex: 0.8,
            minWidth: 140,
            renderCell: (params) => {
                const inventory = params.row?.inventory || [];
                let total = 0;

                if (Array.isArray(inventory)) {
                    total = inventory.reduce((sum, item) => {
                        const qty = parseFloat(item?.quantity) || 0;
                        return sum + qty;
                    }, 0);
                }

                const hasInventory = inventory && inventory.length > 0;
                const status = getStockStatus(total, hasInventory);

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                color: status.color,
                            }}
                        >
                            {total}
                        </Typography>
                        <Chip
                            label={status.label}
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                bgcolor: status.bgColor,
                                color: status.color,
                                border: 'none',
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            field: 'has_variants',
            headerName: 'Variants',
            flex: 0.6,
            minWidth: 90,
            renderCell: (params) => {
                const hasVariants = params.row?.has_variants;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Chip
                            label={hasVariants ? 'Yes' : 'No'}
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                bgcolor: hasVariants
                                    ? alpha(theme.palette.info.main, 0.08)
                                    : alpha(theme.palette.text.disabled, 0.08),
                                color: hasVariants
                                    ? theme.palette.info.main
                                    : theme.palette.text.disabled,
                                border: 'none',
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.4,
            minWidth: 60,
            sortable: false,
            renderCell: (params) => {
                if (!params?.row) return null;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Tooltip title="More actions">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    setSelectedProduct(params.row);
                                    setMenuAnchor(e.currentTarget);
                                }}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        color: 'primary.main',
                                    },
                                }}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            }
        }
    ];

    if (loading) {
        return (
            <PageContainer title="Product Management">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            title="Product Management"
            subtitle="Manage your product catalog"
            actions={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<CategoryIcon />}
                        onClick={handleCategoriesClick}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: 2,
                            px: 2,
                        }}
                    >
                        Manage Categories
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleProductAction('add')}
                        disabled={categories.length === 0}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 2.5,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            },
                        }}
                    >
                        Add Product
                    </Button>
                </Box>
            }
        >
            {combinedError && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.error.main, 0.2),
                    }}
                >
                    {combinedError}
                </Alert>
            )}

            {categories.length === 0 && (
                <Alert
                    severity="info"
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.info.main, 0.2),
                    }}
                >
                    You must create at least one category before adding products.
                </Alert>
            )}

            <Paper
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                }}
            >
                {/* Search & Filter Bar */}
                <Box
                    sx={{
                        p: 2.5,
                        display: 'flex',
                        gap: 2,
                        flexDirection: { xs: 'column', md: 'row' },
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            fullWidth
                            placeholder="Search products by name, SKU, or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: isSearchPending && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={16} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: 'background.paper',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: alpha(theme.palette.primary.main, 0.4),
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Box sx={{ minWidth: { xs: '100%', md: 240 } }}>
                        <TextField
                            fullWidth
                            select
                            label="Filter by Category"
                            value={selectedCategory?.id || ''}
                            onChange={(e) => {
                                const category = categories.find(c => c.id === e.target.value);
                                setSelectedCategory(category || null);
                            }}
                            disabled={categories.length === 0}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FilterListIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: 'background.paper',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: alpha(theme.palette.primary.main, 0.4),
                                    },
                                },
                            }}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </Box>

                {/* Data Table */}
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <DataGrid
                        rows={products}
                        columns={columns}
                        autoHeight
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 10 }
                            }
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        disableRowSelectionOnClick
                        loading={loading}
                        getRowHeight={() => 'auto'}
                        slots={{
                            noRowsOverlay: () => (
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', py: 6, gap: 1 }}>
                                    <Avatar
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                            color: 'primary.main',
                                            mb: 1,
                                        }}
                                    >
                                        <InventoryIcon sx={{ fontSize: 28 }} />
                                    </Avatar>
                                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        {categories.length === 0 ? 'No categories yet' : 'No products found'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {categories.length === 0
                                            ? 'Create a category to start adding products'
                                            : 'Add your first product to get started'}
                                    </Typography>
                                </Box>
                            )
                        }}
                        sx={{
                            border: 'none',
                            // Column headers - distinct background
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.main, 0.12)
                                    : theme.palette.grey[50],
                                borderBottom: '2px solid',
                                borderColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.divider, 0.3)
                                    : theme.palette.grey[300],
                            },
                            '& .MuiDataGrid-columnHeader': {
                                '&:focus, &:focus-within': {
                                    outline: 'none',
                                },
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                whiteSpace: 'nowrap',
                            },
                            // Cell styling - proper padding and text wrapping
                            '& .MuiDataGrid-cell': {
                                borderColor: alpha(theme.palette.divider, 0.5),
                                py: 1.5,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                wordBreak: 'break-word',
                                lineHeight: 1.5,
                                '&:focus, &:focus-within': {
                                    outline: 'none',
                                },
                            },
                            // Alternating row colors (striped rows)
                            '& .MuiDataGrid-row': {
                                transition: 'background-color 0.15s ease-in-out',
                                '&:nth-of-type(even)': {
                                    bgcolor: theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.02)
                                        : alpha(theme.palette.grey[100], 0.5),
                                },
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                                },
                                '&.Mui-selected': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    },
                                },
                            },
                            // Footer / Pagination styling
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '2px solid',
                                borderColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.divider, 0.3)
                                    : theme.palette.grey[300],
                                bgcolor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.main, 0.06)
                                    : theme.palette.grey[50],
                                minHeight: 52,
                            },
                            '& .MuiTablePagination-root': {
                                color: 'text.primary',
                                overflow: 'visible',
                            },
                            '& .MuiTablePagination-toolbar': {
                                minHeight: 52,
                                px: 2,
                            },
                            '& .MuiTablePagination-selectLabel': {
                                fontSize: '0.825rem',
                                fontWeight: 500,
                                color: 'text.secondary',
                            },
                            '& .MuiTablePagination-displayedRows': {
                                fontSize: '0.825rem',
                                fontWeight: 600,
                                color: 'text.primary',
                            },
                            '& .MuiTablePagination-select': {
                                fontSize: '0.825rem',
                                fontWeight: 600,
                            },
                            '& .MuiTablePagination-actions': {
                                ml: 2,
                                '& .MuiIconButton-root': {
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    mx: 0.5,
                                    p: 0.75,
                                    color: 'text.primary',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        borderColor: theme.palette.primary.main,
                                        color: 'primary.main',
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: alpha(theme.palette.divider, 0.3),
                                        color: 'text.disabled',
                                    },
                                },
                            },
                            // Column separator
                            '& .MuiDataGrid-columnSeparator': {
                                color: alpha(theme.palette.divider, 0.3),
                            },
                            // Overlay wrapper for empty state
                            '& .MuiDataGrid-overlayWrapper': {
                                minHeight: 200,
                            },
                        }}
                    />
                </Box>
            </Paper>

            {/* Product Dialog */}
            <ProductDialog
                open={productDialog.open}
                product={productDialog.product}
                categories={categories}
                onClose={handleDialogClose}
            />

            {/* Inventory Setup Dialog */}
            <InventorySetupDialog
                open={inventorySetupDialog.open}
                product={inventorySetupDialog.product}
                onClose={() => setInventorySetupDialog({ open: false, product: null })}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, product: null })}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: alpha(theme.palette.error.main, 0.2),
                            }}
                        >
                            {error}
                        </Alert>
                    )}
                    <Typography>
                        Are you sure you want to delete the product "{deleteDialog.product?.name}"?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        If this product has transaction history, it will be deactivated instead of deleted to preserve data integrity.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={() => {
                            setDeleteDialog({ open: false, product: null });
                            setError(''); // Clear error when canceling
                        }}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: 2,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                            },
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Actions Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => {
                    setMenuAnchor(null);
                    setSelectedProduct(null);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        minWidth: 160,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
                        border: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                <MenuItem
                    onClick={() => handleProductAction('edit', selectedProduct)}
                    sx={{
                        py: 1.25,
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                        },
                    }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Edit"
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setDeleteDialog({ open: true, product: selectedProduct });
                        setMenuAnchor(null);
                    }}
                    sx={{
                        py: 1.25,
                        '&:hover': {
                            bgcolor: alpha(theme.palette.error.main, 0.06),
                        },
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Delete"
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500, color: 'error.main' }}
                    />
                </MenuItem>
            </Menu>

            {/* Success Notification */}
            <Snackbar
                open={!!success}
                autoHideDuration={4000}
                onClose={() => setSuccess('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSuccess('')}
                    severity="success"
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        fontWeight: 500,
                    }}
                >
                    {success}
                </Alert>
            </Snackbar>
        </PageContainer>
    );
};

export default ProductManagement;
